import { useLocation } from "react-router-dom";
import { UseNavItems } from "../../lib/UseNavItems";
import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";

const MobileNavigation = () => {
  const location = useLocation();
  const navItems = UseNavItems;
  const timeoutRef = useRef(null);
  const [activeSection, setActiveSection] = useState("#hello");

  useEffect(() => {
    // Set initial active section based on current hash or default to hello
    const currentHash = location.hash || "#hello";
    setActiveSection(currentHash);

    if (!location.hash) {
      window.location.hash = "#hello";
    }
  }, [location]);

  // Debounced function to update active section
  const debouncedSetActiveSection = useCallback((sectionId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setActiveSection(sectionId);
      // Update URL hash without triggering page jump
      if (window.location.hash !== sectionId) {
        window.history.replaceState(null, null, sectionId);
      }
    }, 80); // Optimized delay for mobile responsiveness
  }, []);

  const visibleSections = useRef(new Set());

  useEffect(() => {
    const observerOptions = {
      root: null, // Use viewport as root for mobile
      rootMargin: "-10% 0px -10% 0px", // Reduced margins for mobile
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1], // Better threshold values
    };

    const observer = new IntersectionObserver((entries) => {
      // Update visibility set
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleSections.current.add(entry.target.id);
        } else {
          visibleSections.current.delete(entry.target.id);
        }
      });

      // Find the section with highest intersection ratio and visibility
      let bestCandidate = null;
      let maxVisibleHeight = 0;

      visibleSections.current.forEach((id) => {
        const element = document.getElementById(id);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Calculate visible area
        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, viewportHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          bestCandidate = id;
        }
      });

      // Fallback: if nothing is clearly visible (e.g. fast scrolling), use the first one in the viewport
      if (!bestCandidate && visibleSections.current.size > 0) {
        bestCandidate = Array.from(visibleSections.current)[0];
      }

      if (bestCandidate) {
        debouncedSetActiveSection(`#${bestCandidate}`);
      }
    }, observerOptions);

    // List of section IDs to observe (matching DOM order in Home.jsx)
    const sections = [
      "hello",
      "experiences",
      "skills",
      "projects",
      "strengths",
      "blogs",
      "contact",
    ];

    // Retry mechanism to ensure elements are observed even if they mount later
    let intervalId;
    const observedSections = new Set();
    
    const observeElements = () => {
      let allObserved = true;
      sections?.forEach((sectionId) => {
        if (observedSections.has(sectionId)) return;
        
        const section = document.getElementById(sectionId);
        if (section) {
          observer.observe(section);
          observedSections.add(sectionId);
        } else {
          allObserved = false;
        }
      });
      return allObserved;
    };

    if (!observeElements()) {
      intervalId = setInterval(() => {
        if (observeElements()) {
          clearInterval(intervalId);
        }
      }, 500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      sections?.forEach((sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
          observer.unobserve(section);
        }
      });

      // Cleanup timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debouncedSetActiveSection]);

  const handleScrollTo = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setActiveSection(`#${id}`);
      window.history.pushState(null, "", `#${id}`);
    }
  };

  const itemRefs = useRef([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const activeIndex = navItems.findIndex(
      (item) => item.path === activeSection
    );
    if (activeIndex !== -1 && itemRefs.current[activeIndex]) {
      const el = itemRefs.current[activeIndex];
      setIndicatorStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
      });
    }
  }, [activeSection, navItems]);

  return (
    <div className="fixed bottom-1 left-1/2 -translate-x-1/2 w-[95%] max-w-[400px] z-50">
      <div className="px-4 py-2 text-gray-100 textFont bg-[#1C1E20]/85 backdrop-blur-xl border border-gray-700/60 shadow-2xl rounded-full">
        <ul className="flex justify-between items-center relative w-full">
          {/* Continuous Active Indicator (Water drop smooth motion) */}
          <motion.div
            className="absolute flex items-center justify-center z-10 pointer-events-none"
            animate={{ left: indicatorStyle.left, width: indicatorStyle.width }}
            transition={{
              type: "spring",
              stiffness: 280,
              damping: 20,
              mass: 0.8,
            }}
          >
            <div className="active-circle w-11 h-11 rounded-full border border-dashed border-[#22D4FD] bg-[#22D4FD]/10 shadow-[0_0_10px_rgba(34,212,253,0.2)] relative"></div>
          </motion.div>

          {navItems?.map((item, index) => (
            <li
              key={index + 1}
              ref={(el) => (itemRefs.current[index] = el)}
              className="relative z-20 flex-1 flex justify-center"
            >
              {item?.external ? (
                <a
                  href={item?.path}
                  target={`_blank`}
                  className="flex items-center justify-center w-11 h-11"
                >
                  <div
                    className={`flex items-center justify-center transition-colors duration-200 text-gray-400 hover:text-gray-200`}
                  >
                    {item?.icon}
                  </div>
                </a>
              ) : (
                <button
                  onClick={() => handleScrollTo(item?.path?.replace("#", ""))}
                  className="flex items-center justify-center w-11 h-11"
                >
                  <div
                    className={`flex items-center justify-center transition-colors duration-200 ${
                      activeSection === item?.path
                        ? "text-[#22D4FD]"
                        : "text-gray-400"
                    }`}
                  >
                    {item?.icon}
                  </div>
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default MobileNavigation;
